/**
 * 项目参数 — the investment programs the recommendation engine chooses from.
 *
 * Backed by SalesRecommendationHub `ListInvestmentPrograms`, which only Deal Desk
 * admins may call (program configuration carries pricing detail). Edit / Create New
 * open ProgramEditor, which saves through Update/CreateInvestmentProgram — a saved
 * program changes live recommendations at once, so the editor confirms the changes
 * first.
 *
 * The whole set (tens of rows) is loaded once, so search, sorting and paging are
 * client-side via useCollection.
 */
import { type CSSProperties, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CollectionPreferences,
  type CollectionPreferencesProps,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  Table,
  type TableProps,
  TextFilter,
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import ProgramEditor from './ProgramEditor';
import {
  colorBorderDividerDefault,
  colorChartsPaletteCategorical1,
  colorTextLinkDefault,
} from '@amzn/awsui-design-tokens/polaris';
import { useTranslation } from 'react-i18next';

import type { InvestmentProgramData } from '../../client/CpaClient';
import { usePrograms } from '../../state/usePrograms';
import { amountRange, parseContacts, ratioBounds, searchText, usd } from '../../utils/programs';
import { Cycle, Discount, Empty, People, Priority, ProgramName, Sla } from './cells';

const PPA_LABEL_KEYS = {
  non_ppa: 'ppaNonPpa',
  sce_led_cross_service: 'ppaSceLedCrossService',
  seller_led_cross_service: 'ppaSellerLedCrossService',
  sce_led_service_specific: 'ppaSceLedServiceSpecific',
  seller_led_service_specific: 'ppaSellerLedServiceSpecific',
  r_ppa: 'ppaRPpa',
} as const;

const CYCLE_LABEL_KEYS = {
  recurring: 'cycleRecurring',
  one_time: 'cycleOneTime',
} as const;

/** Label key for a known enum value; `undefined` for absent or unknown values. */
function labelKey<K extends string, V>(keys: Record<K, V>, value: string | undefined): V | undefined {
  return value !== undefined && Object.prototype.hasOwnProperty.call(keys, value)
    ? keys[value as K]
    : undefined;
}

/** Design tokens handed to ProgramsPage.css (Cloudscape's own custom-property names are hashed). */
const TOKEN_VARS = {
  '--cpa-divider': colorBorderDividerDefault,
  '--cpa-bar': colorChartsPaletteCategorical1,
  '--cpa-link': colorTextLinkDefault,
} as CSSProperties;

const DEFAULT_COLUMNS = ['priority', 'name', 'cycle', 'ratio', 'amount', 'poc', 'sla'];
// Hidden by default; the table preferences can turn them on.
const OPTIONAL_COLUMNS = ['type', 'qualifier', 'exclusive', 'links', 'updated'];

const nullsLast = (a: number | undefined, b: number | undefined) =>
  (a ?? Number.MAX_SAFE_INTEGER) - (b ?? Number.MAX_SAFE_INTEGER);

export default function ProgramsPage() {
  const { t } = useTranslation('cpa');
  const { state, reload: load } = usePrograms();
  const [editing, setEditing] = useState<{ program?: InvestmentProgramData } | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 20,
    visibleContent: DEFAULT_COLUMNS,
  });

  const programs = state.kind === 'ready' ? state.programs : [];
  const { items, filteredItemsCount, collectionProps, filterProps, paginationProps, actions } = useCollection(
    programs,
    {
      filtering: {
        filteringFunction: (p, text) => searchText(p).includes(text.trim().toLowerCase()),
        empty: (
          <Box textAlign="center" color="inherit">
            {t('programsEmpty')}
          </Box>
        ),
        noMatch: (
          <SpaceBetween size="s" alignItems="center">
            <Box color="inherit">{t('programsNoMatch')}</Box>
            <Button onClick={() => actions.setFiltering('')}>{t('programsClearFilter')}</Button>
          </SpaceBetween>
        ),
      },
      // Default order is the service's: priority, then name.
      sorting: {},
      selection: { trackBy: 'programId' },
      pagination: { pageSize: preferences.pageSize },
    },
  );

  const columns: TableProps.ColumnDefinition<InvestmentProgramData>[] = [
    {
      id: 'priority',
      header: t('colPriority'),
      cell: (p) => <Priority priority={p.priority} />,
      sortingComparator: (a, b) => nullsLast(a.priority, b.priority),
      width: 96,
    },
    {
      id: 'name',
      header: t('colName'),
      cell: (p) => <ProgramName name={p.name} programId={p.programId} wiki={p.wikis?.[0]} />,
      sortingField: 'name',
      isRowHeader: true,
      minWidth: 240,
    },
    {
      id: 'type',
      header: t('colType'),
      cell: (p) => {
        const key = labelKey(PPA_LABEL_KEYS, p.ppaType);
        if (!key) return <Empty />;
        return <Badge color={p.ppaType === 'non_ppa' ? 'grey' : 'blue'}>{t(key)}</Badge>;
      },
      sortingField: 'ppaType',
      width: 190,
    },
    {
      id: 'cycle',
      header: t('colCycle'),
      cell: (p) => {
        const key = labelKey(CYCLE_LABEL_KEYS, p.discountCycle);
        return <Cycle cycle={p.discountCycle} label={key && t(key)} />;
      },
      sortingField: 'discountCycle',
      width: 110,
    },
    {
      id: 'ratio',
      header: t('colRatio'),
      cell: (p) => (
        <Discount method={p.discountMethod} noLimitLabel={t('noLimit')} tieredLabel={t('tiered')} />
      ),
      sortingComparator: (a, b) =>
        (ratioBounds(a.discountMethod)?.max ?? -1) - (ratioBounds(b.discountMethod)?.max ?? -1),
      width: 140,
    },
    {
      id: 'amount',
      header: t('colAmount'),
      cell: (p) => {
        if (p.discountMethod?.discountType === 'no_limit') return t('noLimit');
        const amount = amountRange(p.discountMethod);
        return amount ? <span className="cpa-programs-num">{amount}</span> : <Empty />;
      },
      width: 140,
    },
    {
      id: 'poc',
      header: t('colPoc'),
      cell: (p) => <People contacts={parseContacts(p.contacts)} />,
      minWidth: 160,
    },
    {
      id: 'sla',
      header: t('colSla'),
      cell: (p) => <Sla sla={p.sla} />,
      sortingField: 'sla',
      width: 170,
    },
    {
      id: 'qualifier',
      header: t('colQualifier'),
      cell: (p) => {
        const q = p.qualifier;
        if (q?.min == null && q?.max == null) return <Empty />;
        if (q.max == null) return `≥ ${usd(q.min!)}`;
        if (q.min == null) return `≤ ${usd(q.max)}`;
        return `${usd(q.min)} - ${usd(q.max)}`;
      },
      sortingComparator: (a, b) => nullsLast(a.qualifier?.min, b.qualifier?.min),
    },
    {
      id: 'exclusive',
      header: t('colExclusive'),
      cell: (p) => {
        const entries = Object.entries(p.mutuallyExclusive ?? {}).filter(([, names]) => names?.length);
        if (entries.length === 0) return <Empty />;
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            {entries.flatMap(([scope, names]) =>
              names!.map((name) => (
                <Badge key={`${scope}:${name}`} color="severity-low">
                  {scope} · {name}
                </Badge>
              )),
            )}
          </SpaceBetween>
        );
      },
    },
    {
      id: 'links',
      header: t('colLinks'),
      cell: (p) => {
        const links = [
          ...(p.wikis ?? []).map((href) => ({ href, label: t('linkWiki') })),
          ...(p.applicationLinks ?? []).map((href) => ({
            href,
            label: t('linkApply'),
          })),
        ];
        if (links.length === 0) return <Empty />;
        return (
          <SpaceBetween direction="horizontal" size="xs">
            {links.map(({ href, label }) => (
              <Link key={href} href={href} external>
                {label}
              </Link>
            ))}
          </SpaceBetween>
        );
      },
    },
    {
      id: 'updated',
      header: t('colUpdated'),
      cell: (p) => (p.updatedAt ? p.updatedAt.toLocaleDateString() : <Empty />),
      sortingComparator: (a, b) => (a.updatedAt?.getTime() ?? 0) - (b.updatedAt?.getTime() ?? 0),
    },
  ];

  if (state.kind === 'denied') {
    return <Alert type="warning">{t('programsDenied')}</Alert>;
  }
  if (state.kind === 'error') {
    return (
      <Alert
        type="error"
        header={t('programsLoadError')}
        action={<Button onClick={load}>{t('programsRetry')}</Button>}
      >
        {state.message}
      </Alert>
    );
  }

  return (
    <div className="cpa-programs" style={TOKEN_VARS}>
      {saved && (
        <Box margin={{ bottom: 's' }}>
          <Alert type="success" dismissible onDismiss={() => setSaved(null)}>
            {t('editorSaved', { name: saved })}
          </Alert>
        </Box>
      )}
      {editing && (
        <ProgramEditor
          program={editing.program}
          allPrograms={programs}
          onClose={() => setEditing(null)}
          onSaved={(p) => {
            setEditing(null);
            setSaved(p.name ?? p.programId ?? '');
            actions.setSelectedItems([]);
            load();
          }}
        />
      )}
      <Table
        {...collectionProps}
        variant="container"
        selectionType="single"
        items={items}
        columnDefinitions={columns}
        visibleColumns={preferences.visibleContent}
        loading={state.kind === 'loading'}
        loadingText={t('programsLoading')}
        trackBy="programId"
        resizableColumns
        stripedRows
        header={
          <Header
            variant="h2"
            counter={state.kind === 'ready' ? `(${programs.length})` : undefined}
            description={t('programsCaption')}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  disabled={collectionProps.selectedItems?.length !== 1}
                  onClick={() => setEditing({ program: collectionProps.selectedItems![0] })}
                >
                  {t('programsEdit')}
                </Button>
                <Button variant="primary" onClick={() => setEditing({})}>
                  {t('programsCreate')}
                </Button>
              </SpaceBetween>
            }
          >
            {t('tabPrograms')}
          </Header>
        }
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder={t('programsSearch')}
            countText={t('programsMatches', { count: filteredItemsCount ?? 0 })}
          />
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title={t('prefsTitle')}
            confirmLabel={t('prefsConfirm')}
            cancelLabel={t('cancel')}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={{
              title: t('prefsPageSize'),
              options: [10, 20, 50].map((value) => ({
                value,
                label: String(value),
              })),
            }}
            visibleContentPreference={{
              title: t('prefsColumns'),
              options: [
                {
                  label: t('prefsColumns'),
                  options: [...DEFAULT_COLUMNS, ...OPTIONAL_COLUMNS].map((id) => ({
                    id,
                    label: String(columns.find((c) => c.id === id)?.header ?? id),
                    editable: id !== 'name',
                  })),
                },
              ],
            }}
          />
        }
      />
    </div>
  );
}
